<template>
  <div id="app" class="flex h-screen overflow-hidden w-full">
    <!-- Top Menu Bar -->
    <AppTopbar
      v-if="isAuthenticatedPage"
      :show-sidebar="isAuthenticatedPage"
      @toggle-sidebar="toggleSidebar"
    />

    <!-- Overlay for mobile when sidebar is open -->
    <div
      v-if="isAuthenticatedPage && isMobile && isSidebarOpen"
      class="fixed inset-0 bg-black bg-opacity-50 z-40"
      @click="closeSidebar"
    ></div>

    <!-- Sidebar -->
    <Sidebar
      v-if="isAuthenticatedPage"
      :is-open="isSidebarOpen"
      :is-mobile="isMobile"
      @close="closeSidebar"
    />

    <!-- Main Content -->
    <main
      :class="[
        'overflow-y-auto bg-gray-50 dark:bg-gray-950 transition-all duration-300 w-full flex flex-col',
        isAuthenticatedPage ? 'mt-12' : 'mt-0',
        isAuthenticatedPage && !isMobile && isSidebarOpen ? 'ml-64' : 'ml-0'
      ]"
      :style="{ height: isAuthenticatedPage ? 'calc(100vh - 3rem)' : '100vh' }"
    >
      <router-view class="shrink-0" />
    </main>

    <!-- Toast Component -->
    <Toast />

    <!-- Confirmation Dialog Component -->
    <ConfirmDialog />

    <!-- Force Password Change Modal -->
    <div
      v-if="authStore.user?.mustChangePassword && authStore.isAuthenticated"
      class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]"
    >
      <div class="bg-white rounded-lg border border-gray-200 max-w-md w-full mx-4">
        <div class="p-6">
          <div class="mb-6">
            <div
              class="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full"
            >
              <svg
                class="w-6 h-6 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 class="text-2xl font-bold text-gray-900 text-center">Password Change Required</h2>
            <p class="text-sm text-gray-500 mt-2 text-center">
              For security reasons, you must change your password before continuing.
            </p>
          </div>

          <form class="space-y-4" @submit.prevent="handleForcePasswordChange">
            <div>
              <label for="force-currentPassword" class="block text-sm font-medium text-gray-700"
                >Current Password</label
              >
              <input
                id="force-currentPassword"
                v-model="forcePasswordForm.currentPassword"
                type="password"
                required
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label for="force-newPassword" class="block text-sm font-medium text-gray-700"
                >New Password</label
              >
              <input
                id="force-newPassword"
                v-model="forcePasswordForm.newPassword"
                type="password"
                required
                minlength="6"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter new password (min. 6 characters)"
              />
            </div>

            <div>
              <label for="force-confirmPassword" class="block text-sm font-medium text-gray-700"
                >Confirm New Password</label
              >
              <input
                id="force-confirmPassword"
                v-model="forcePasswordForm.confirmPassword"
                type="password"
                required
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Confirm new password"
              />
            </div>

            <div
              v-if="forcePasswordError"
              class="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3"
            >
              {{ forcePasswordError }}
            </div>

            <div class="flex justify-center pt-4">
              <button
                type="submit"
                :disabled="forcePasswordLoading"
                class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {{ forcePasswordLoading ? "Changing..." : "Change Password" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "./stores/auth";
import Sidebar from "./components/common/Sidebar.vue";
import AppTopbar from "./components/common/AppTopbar.vue";
import Toast from "primevue/toast";
import ConfirmDialog from "primevue/confirmdialog";

export default {
  name: "App",
  components: {
    Sidebar,
    AppTopbar,
    Toast,
    ConfirmDialog
  },
  setup() {
    const route = useRoute();
    const authStore = useAuthStore();
    // Initialize sidebar state from localStorage (for desktop persistence)
    const isSidebarOpen = ref(localStorage.getItem("sidebarClosed") !== "true");
    const isMobile = ref(false);
    const forcePasswordForm = ref({
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
    const forcePasswordError = ref("");
    const forcePasswordLoading = ref(false);

    // Check screen size and update mobile state
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 800;
      // On desktop, default sidebar to open (unless explicitly closed)
      // On mobile, default sidebar to closed
      if (
        !isMobile.value &&
        isSidebarOpen.value === false &&
        localStorage.getItem("sidebarClosed") !== "true"
      ) {
        // Don't auto-open if user explicitly closed it
      }
    };

    // Check if current page is authenticated (shows sidebar/topbar)
    // Use auth state + route check to avoid animation issues during login/logout
    const isAuthenticatedPage = computed(() => {
      if (!authStore.isAuthenticated) return false;
      const noSidebarRoutes = ["/login", "/register"];
      return !noSidebarRoutes.includes(route.path);
    });

    const toggleSidebar = () => {
      isSidebarOpen.value = !isSidebarOpen.value;
      // Save sidebar state for desktop
      if (!isMobile.value) {
        localStorage.setItem("sidebarClosed", isSidebarOpen.value ? "false" : "true");
      }
    };

    const closeSidebar = () => {
      isSidebarOpen.value = false;
      if (!isMobile.value) {
        localStorage.setItem("sidebarClosed", "true");
      }
    };

    // Close sidebar on route change (mobile only)
    watch(
      () => route.path,
      () => {
        if (isMobile.value) {
          closeSidebar();
        }
      }
    );

    // Handle resize events
    onMounted(() => {
      checkMobile();
      window.addEventListener("resize", checkMobile);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", checkMobile);
    });

    const handleForcePasswordChange = async () => {
      if (forcePasswordForm.value.newPassword !== forcePasswordForm.value.confirmPassword) {
        forcePasswordError.value = "New passwords do not match";
        return;
      }

      if (forcePasswordForm.value.newPassword.length < 6) {
        forcePasswordError.value = "Password must be at least 6 characters long";
        return;
      }

      try {
        forcePasswordLoading.value = true;
        forcePasswordError.value = "";

        const result = await authStore.changePassword({
          currentPassword: forcePasswordForm.value.currentPassword,
          newPassword: forcePasswordForm.value.newPassword
        });

        if (result.success) {
          // Password changed successfully, modal will disappear automatically
          // because mustChangePassword flag will be cleared
          forcePasswordForm.value = {
            currentPassword: "",
            newPassword: "",
            confirmPassword: ""
          };
        } else {
          forcePasswordError.value = result.message || "Failed to change password";
        }
      } catch (err) {
        forcePasswordError.value = "An unexpected error occurred";
      } finally {
        forcePasswordLoading.value = false;
      }
    };

    return {
      authStore,
      isAuthenticatedPage,
      isSidebarOpen,
      isMobile,
      toggleSidebar,
      closeSidebar,
      forcePasswordForm,
      forcePasswordError,
      forcePasswordLoading,
      handleForcePasswordChange
    };
  }
};
</script>

<style>
#app {
  font-family:
    "Roboto Flex",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    sans-serif;
}
</style>
